import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import "./PaymentGateway.scss";

const extractError = (err, fallback = "Request failed") =>
  err?.response?.data?.error ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

const unwrapData = (res) => (res?.data != null ? res.data : res);

const formatCurrency = (amount, currency = "INR") =>
  `${currency} ${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const paymentApprovalHint = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s === "pending") {
    return "Payment approval is pending. Complete approval before paying.";
  }
  if (s === "rejected") {
    return "Payment approval was rejected. Contact your organisation admin.";
  }
  return null;
};

export default function PaymentGateway() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const afterSuccessNavigate = location.state?.afterSuccessNavigate || null;

  const [paymentMethod, setPaymentMethod] = useState("Debit / Credit Card");
  const [loading, setLoading] = useState(false);
  const [grnCheckoutLoading, setGrnCheckoutLoading] = useState(false);
  const [grnCheckout, setGrnCheckout] = useState(null);

  const [cardDetails, setCardDetails] = useState({
    cardHolderName: "",
    cardNumber: "",
    cardExpiry: "",
    cvv: "",
  });

  const [netBankingDetails, setNetBankingDetails] = useState({
    selectedBank: "",
    userId: "",
  });

  const [upiDetails, setUpiDetails] = useState({
    upiId: "",
  });

  const banks = [
    "State Bank of India (SBI)",
    "HDFC Bank",
    "ICICI Bank",
    "Axis Bank",
    "Kotak Mahindra Bank",
    "Punjab National Bank (PNB)",
    "Bank of Baroda",
    "Canara Bank",
    "Union Bank of India",
    "Indian Bank",
    "Bank of India",
    "IDBI Bank",
    "Central Bank of India",
    "Indian Overseas Bank",
    "Yes Bank",
    "Federal Bank",
    "South Indian Bank",
    "Karur Vysya Bank",
    "City Union Bank",
    "DCB Bank",
  ];

  const paymentData = location.state?.paymentData || null;
  const paymentId = location.state?.paymentId || null;
  const marketplacePoPayment = location.state?.marketplacePoPayment || null;
  const marketplacePoPayments = useMemo(() => {
    if (Array.isArray(location.state?.marketplacePoPayments) && location.state.marketplacePoPayments.length) {
      return location.state.marketplacePoPayments
        .map((entry) => ({
          purchaseOrderId: Number(entry.purchaseOrderId),
          poNumber: entry.poNumber,
          amount: Number(entry.amount),
          currency: entry.currency || "INR",
          productName: entry.productName,
        }))
        .filter((entry) => Number.isFinite(entry.purchaseOrderId) && entry.purchaseOrderId > 0);
    }
    if (
      marketplacePoPayment &&
      Number.isFinite(Number(marketplacePoPayment.purchaseOrderId)) &&
      Number(marketplacePoPayment.purchaseOrderId) > 0
    ) {
      return [
        {
          purchaseOrderId: Number(marketplacePoPayment.purchaseOrderId),
          poNumber: marketplacePoPayment.poNumber,
          amount: Number(marketplacePoPayment.amount),
          currency: marketplacePoPayment.currency || "INR",
          productName: null,
        },
      ];
    }
    return [];
  }, [location.state, marketplacePoPayment]);
  const marketplacePoId = marketplacePoPayments[0]?.purchaseOrderId ?? null;

  const grnId = useMemo(() => {
    const fromState = location.state?.grnPayment?.grnId;
    if (fromState != null && Number.isFinite(Number(fromState)) && Number(fromState) > 0) {
      return Number(fromState);
    }
    const fromQuery = searchParams.get("grnId");
    if (fromQuery != null && Number.isFinite(Number(fromQuery)) && Number(fromQuery) > 0) {
      return Number(fromQuery);
    }
    return null;
  }, [location.state, searchParams]);

  const loadGrnCheckout = useCallback(async () => {
    if (!grnId) {
      setGrnCheckout(null);
      return;
    }
    setGrnCheckoutLoading(true);
    try {
      const res = await CommonService.get(`/grn/${grnId}/payment-summary`);
      const data = unwrapData(res);
      if (!data) {
        throw new Error("Could not load payment details");
      }
      setGrnCheckout(data);
    } catch (err) {
      setGrnCheckout(null);
      toast.error(extractError(err, "Could not load GRN payment details"));
    } finally {
      setGrnCheckoutLoading(false);
    }
  }, [grnId]);

  useEffect(() => {
    loadGrnCheckout();
  }, [loadGrnCheckout]);

  const grnPaid =
    grnCheckout &&
    String(grnCheckout.payment_status || "").trim().toLowerCase() === "paid";

  const grnCanPay = grnCheckout?.can_pay === true;

  const grnBlockReason = useMemo(() => {
    if (!grnId || !grnCheckout || grnPaid) return null;
    if (grnCanPay) return null;
    return (
      paymentApprovalHint(grnCheckout.payment_approval_status) ||
      "Payment is not available for this GRN yet. Ensure the seller has accepted the GRN and any required payment approval is complete."
    );
  }, [grnId, grnCheckout, grnPaid, grnCanPay]);

  const summaryTitle =
    paymentData?.summaryTitle ||
    paymentData?.title ||
    (grnCheckout?.grn_number
      ? `Marketplace payment · ${grnCheckout.grn_number}`
      : grnId
        ? `Marketplace payment · GRN #${grnId}`
        : "");

  const summaryAmount =
    paymentData?.summaryAmount ||
    paymentData?.amountLabel ||
    (grnCheckout?.payment_amount != null && Number(grnCheckout.payment_amount) > 0
      ? formatCurrency(grnCheckout.payment_amount)
      : "");

  const summaryLines = useMemo(() => {
    if (Array.isArray(paymentData?.summaryLines) && paymentData.summaryLines.length > 0) {
      return paymentData.summaryLines;
    }
    if (!grnCheckout) return [];
    const lines = [];
    if (grnCheckout.purchase_order_id) {
      lines.push(`PO: #${grnCheckout.purchase_order_id}`);
    }
    if (grnCheckout.invoice_number) {
      lines.push(`Invoice: ${grnCheckout.invoice_number}`);
    }
    if (grnCheckout.grn_number) {
      lines.push(`GRN: ${grnCheckout.grn_number}`);
    }
    if (grnCheckout.supplier_name) {
      lines.push(`Supplier: ${grnCheckout.supplier_name}`);
    }
    return lines;
  }, [paymentData, grnCheckout]);

  const hasCheckoutContext =
    grnId != null ||
    marketplacePoPayments.length > 0 ||
    paymentId != null ||
    location.state?.logisticsPayment != null ||
    summaryTitle ||
    summaryAmount ||
    summaryLines.length > 0;

  const paymentDisabled =
    loading ||
    grnCheckoutLoading ||
    (grnId != null && (!grnCheckout || grnPaid || !grnCanPay));

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setCardDetails({
      cardHolderName: "",
      cardNumber: "",
      cardExpiry: "",
      cvv: "",
    });
    setNetBankingDetails({
      selectedBank: "",
      userId: "",
    });
    setUpiDetails({
      upiId: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "cardNumber") {
      const formatted = value.replace(/\s/g, "").replace(/(.{4})/g, "$1 ").trim();
      if (formatted.length <= 19) {
        setCardDetails((prev) => ({ ...prev, [name]: formatted }));
      }
    } else if (name === "cardExpiry") {
      const formatted = value.replace(/\D/g, "").replace(/(\d{2})(\d)/, "$1/$2").substring(0, 5);
      setCardDetails((prev) => ({ ...prev, [name]: formatted }));
    } else if (name === "cvv") {
      const formatted = value.replace(/\D/g, "").substring(0, 4);
      setCardDetails((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setCardDetails((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateCardDetails = () => {
    if (!cardDetails.cardHolderName.trim()) {
      toast.error("Please enter card holder name");
      return false;
    }
    if (!cardDetails.cardNumber.replace(/\s/g, "") || cardDetails.cardNumber.replace(/\s/g, "").length < 16) {
      toast.error("Please enter a valid card number");
      return false;
    }
    if (!cardDetails.cardExpiry || cardDetails.cardExpiry.length !== 5) {
      toast.error("Please enter a valid expiry date (MM/YY)");
      return false;
    }
    if (!cardDetails.cvv || cardDetails.cvv.length < 3) {
      toast.error("Please enter a valid CVV");
      return false;
    }
    return true;
  };

  const validateNetBanking = () => {
    if (!netBankingDetails.selectedBank) {
      toast.error("Please select a bank");
      return false;
    }
    if (!netBankingDetails.userId.trim()) {
      toast.error("Please enter your NetBanking User ID");
      return false;
    }
    return true;
  };

  const validateUPI = () => {
    if (!upiDetails.upiId.trim()) {
      toast.error("Please enter your UPI ID");
      return false;
    }
    const upiPattern = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
    if (!upiPattern.test(upiDetails.upiId.trim())) {
      toast.error("Please enter a valid UPI ID (e.g., yourname@paytm)");
      return false;
    }
    return true;
  };

  const handleSubmitPayment = async () => {
    if (grnId != null && grnBlockReason) {
      toast.error(grnBlockReason);
      return;
    }

    try {
      setLoading(true);
      let paymentReference = "";

      if (paymentMethod === "Debit / Credit Card") {
        if (!validateCardDetails()) {
          setLoading(false);
          return;
        }
        paymentReference = `CARD-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      } else if (paymentMethod === "NetBanking") {
        if (!validateNetBanking()) {
          setLoading(false);
          return;
        }
        paymentReference = `NB-${netBankingDetails.selectedBank.substring(0, 4).toUpperCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
      } else if (paymentMethod === "UPI ID") {
        if (!validateUPI()) {
          setLoading(false);
          return;
        }
        paymentReference = `UPI-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      }

      const logisticsPayment = location.state?.logisticsPayment || null;
      const logisticsSubmissionId =
        logisticsPayment && Number.isFinite(Number(logisticsPayment.submissionId))
          ? Number(logisticsPayment.submissionId)
          : null;

      const isAuctionCheckout =
        auctionId != null && grnId == null && logisticsSubmissionId == null && marketplacePoId == null;

      if (isAuctionCheckout) {
        const completeResponse = await CommonService.post(
          API_URLS.AUCTION.COMPLETE_AUCTION_PAYMENT(auctionId),
          {
            payment_reference: paymentReference,
            payment_method:
              location.state?.paymentMethod ||
              paymentData?.payment_method ||
              paymentMethod,
          },
        );
        if (!completeResponse?.success) {
          throw new Error(completeResponse?.message || "Failed to record auction payment");
        }
      } else if (paymentId != null && paymentId !== "") {
        const updateData = {
          payment_reference: paymentReference,
          payment_status: "completed",
        };

        const updateResponse = await CommonService.put(
          API_URLS.AUCTION.UPDATE_PAYMENT(paymentId),
          updateData,
        );

        if (!updateResponse?.success) {
          throw new Error(updateResponse?.message || "Failed to update payment status");
        }
      }

      if (logisticsSubmissionId != null && logisticsSubmissionId > 0) {
        try {
          const markPaidResp = await CommonService.post(
            API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_INVOICE_MARK_PAID(logisticsSubmissionId),
            { paymentReference },
          );
          if (!markPaidResp || markPaidResp.success === false) {
            console.error("Failed to mark logistics invoice paid:", markPaidResp);
          }
        } catch (markPaidErr) {
          console.error("Error marking logistics invoice paid:", markPaidErr);
        }
      }

      if (grnId != null && grnId > 0) {
        try {
          const markGrnPaidResp = await CommonService.post(`/grn/${grnId}/mark-paid`, {
            paymentReference,
            payment_method: paymentMethod,
          });
          if (!markGrnPaidResp || markGrnPaidResp.success === false) {
            toast.error(markGrnPaidResp?.error || "Could not record GRN payment");
            setLoading(false);
            return;
          }
        } catch (markGrnPaidErr) {
          toast.error(extractError(markGrnPaidErr, "Could not record GRN payment"));
          setLoading(false);
          return;
        }
      }

      if (marketplacePoPayments.length > 0) {
        try {
          for (const poPayment of marketplacePoPayments) {
            const markPoPaidResp = await CommonService.post(
              `/hub-home/checkout/purchase-order/${poPayment.purchaseOrderId}/payment`,
              {
                payment_reference: paymentReference,
                payment_method: paymentMethod,
              },
            );
            if (!markPoPaidResp || markPoPaidResp.success === false) {
              toast.error(
                markPoPaidResp?.message ||
                  markPoPaidResp?.error ||
                  `Could not record payment for ${poPayment.poNumber || `PO #${poPayment.purchaseOrderId}`}`,
              );
              setLoading(false);
              return;
            }
          }
        } catch (markPoPaidErr) {
          toast.error(extractError(markPoPaidErr, "Could not record PO payment"));
          setLoading(false);
          return;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast.success("Payment completed successfully!");

      setTimeout(() => {
        if (afterSuccessNavigate) {
          navigate(afterSuccessNavigate, {
            replace: true,
            state: location.state?.afterSuccessState ?? undefined,
          });
        } else if (auctionId && isAuctionCheckout) {
          navigate(`/auction/${auctionId}/collection-delivery`, { replace: true });
        } else if (auctionId) {
          navigate(`/auction/${auctionId}/quality-evaluator-prompt`, {
            state: {
              paymentId,
              auctionId,
            },
          });
        } else if (grnId) {
          navigate("/grn-manifest-list", { replace: true });
        } else if (marketplacePoPayments.length > 0) {
          navigate(afterSuccessNavigate || "/po-status-tracking", {
            replace: true,
            state: location.state?.afterSuccessState ?? { poId: marketplacePoPayments[0].purchaseOrderId },
          });
        } else {
          navigate("/dashboard/buyer", { replace: true });
        }
      }, 800);
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error(extractError(error, "Payment processing failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const submitButtonLabel = () => {
    if (loading) return "Processing...";
    if (grnCheckoutLoading) return "Loading...";
    if (grnPaid) return "Already paid";
    if (paymentMethod === "NetBanking") return "Proceed to Bank Login";
    return "Submit Payment";
  };

  return (
    <div className="payment-gateway-page">
      <div className="payment-gateway-container">
        <button type="button" className="back-btn" onClick={handleBack}>
          ← Back
        </button>

        {!hasCheckoutContext && !grnCheckoutLoading ? (
          <div className="payment-gateway-order-summary">
            <h3 className="payment-gateway-order-summary__title">No payment selected</h3>
            <p className="payment-gateway-order-summary__meta-text">
              Open Pay Now from a GRN, invoice, or auction payment screen to start checkout.
            </p>
          </div>
        ) : null}

        {(summaryTitle || summaryAmount || summaryLines.length > 0 || grnCheckoutLoading) ? (
          <div className="payment-gateway-order-summary">
            {grnCheckoutLoading ? (
              <p className="payment-gateway-order-summary__meta-text">Loading payment details…</p>
            ) : null}
            {summaryTitle ? (
              <h3 className="payment-gateway-order-summary__title">{summaryTitle}</h3>
            ) : null}
            {summaryAmount ? (
              <p className="payment-gateway-order-summary__amount">{summaryAmount}</p>
            ) : null}
            {summaryLines.length > 0 ? (
              <ul className="payment-gateway-order-summary__meta">
                {summaryLines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            ) : null}
            {grnPaid ? (
              <p className="payment-gateway-order-summary__meta-text payment-gateway-order-summary__paid">
                This GRN has already been paid.
              </p>
            ) : null}
            {grnBlockReason ? (
              <p className="payment-gateway-order-summary__meta-text payment-gateway-order-summary__blocked">
                {grnBlockReason}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="payment-gateway-layout">
          <div className="pay-with-section">
            <h2 className="section-title">Pay With</h2>

            <div className="payment-options">
              <button
                type="button"
                className={`payment-option ${paymentMethod === "NetBanking" ? "active" : ""}`}
                onClick={() => handlePaymentMethodChange("NetBanking")}
              >
                <div className="option-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <span>NetBanking</span>
              </button>

              <button
                type="button"
                className={`payment-option ${paymentMethod === "Debit / Credit Card" ? "active" : ""}`}
                onClick={() => handlePaymentMethodChange("Debit / Credit Card")}
              >
                <div className="option-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                </div>
                <span>Debit / Credit Card</span>
              </button>

              <button
                type="button"
                className={`payment-option ${paymentMethod === "UPI ID" ? "active" : ""}`}
                onClick={() => handlePaymentMethodChange("UPI ID")}
              >
                <div className="option-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                  </svg>
                </div>
                <span>UPI ID</span>
              </button>
            </div>
          </div>

          <div className="payment-method-section">
            <h2 className="section-title">Payment Method</h2>

            {paymentMethod === "Debit / Credit Card" && (
              <div className="card-details-form">
                <p className="form-subtitle">Enter Your Card Details</p>

                <div className="form-group">
                  <label htmlFor="cardHolderName">Card Holder Name</label>
                  <input
                    type="text"
                    id="cardHolderName"
                    name="cardHolderName"
                    value={cardDetails.cardHolderName}
                    onChange={handleInputChange}
                    placeholder="Card Holder Name"
                    maxLength={50}
                    disabled={paymentDisabled}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cardNumber">Card Number</label>
                  <input
                    type="text"
                    id="cardNumber"
                    name="cardNumber"
                    value={cardDetails.cardNumber}
                    onChange={handleInputChange}
                    placeholder="Card Number"
                    maxLength={19}
                    disabled={paymentDisabled}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cardExpiry">Card Expiry</label>
                    <input
                      type="text"
                      id="cardExpiry"
                      name="cardExpiry"
                      value={cardDetails.cardExpiry}
                      onChange={handleInputChange}
                      placeholder="MM/YY"
                      maxLength={5}
                      disabled={paymentDisabled}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="cvv">CVV</label>
                    <input
                      type="text"
                      id="cvv"
                      name="cvv"
                      value={cardDetails.cvv}
                      onChange={handleInputChange}
                      placeholder="CVV"
                      maxLength={4}
                      disabled={paymentDisabled}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="submit-payment-btn"
                  onClick={handleSubmitPayment}
                  disabled={paymentDisabled}
                >
                  {submitButtonLabel()}
                </button>
              </div>
            )}

            {paymentMethod === "NetBanking" && (
              <div className="netbanking-form">
                <p className="form-subtitle">Select Your Bank and Enter User ID</p>

                <div className="form-group">
                  <label htmlFor="selectedBank">Select Bank</label>
                  <select
                    id="selectedBank"
                    name="selectedBank"
                    value={netBankingDetails.selectedBank}
                    onChange={(e) =>
                      setNetBankingDetails((prev) => ({ ...prev, selectedBank: e.target.value }))
                    }
                    className="bank-select"
                    disabled={paymentDisabled}
                  >
                    <option value="">-- Select Bank --</option>
                    {banks.map((bank, index) => (
                      <option key={index} value={bank}>
                        {bank}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="userId">NetBanking User ID</label>
                  <input
                    type="text"
                    id="userId"
                    name="userId"
                    value={netBankingDetails.userId}
                    onChange={(e) =>
                      setNetBankingDetails((prev) => ({ ...prev, userId: e.target.value }))
                    }
                    placeholder="Enter your NetBanking User ID"
                    maxLength={50}
                    disabled={paymentDisabled}
                  />
                </div>

                <div className="info-box">
                  <div className="info-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </div>
                  <p>You will be redirected to your bank&apos;s secure login page to complete the payment.</p>
                </div>

                <button
                  type="button"
                  className="submit-payment-btn"
                  onClick={handleSubmitPayment}
                  disabled={paymentDisabled}
                >
                  {submitButtonLabel()}
                </button>
              </div>
            )}

            {paymentMethod === "UPI ID" && (
              <div className="upi-form">
                <p className="form-subtitle">Enter Your UPI ID</p>

                <div className="form-group">
                  <label htmlFor="upiId">UPI ID</label>
                  <input
                    type="text"
                    id="upiId"
                    name="upiId"
                    value={upiDetails.upiId}
                    onChange={(e) => setUpiDetails((prev) => ({ ...prev, upiId: e.target.value }))}
                    placeholder="yourname@paytm / yourname@phonepe / yourname@ybl"
                    maxLength={100}
                    disabled={paymentDisabled}
                  />
                  <small className="input-hint">
                    Format: yourname@paytm, yourname@phonepe, yourname@ybl, etc.
                  </small>
                </div>

                <div className="upi-apps">
                  <p className="apps-label">Supported UPI Apps:</p>
                  <div className="apps-grid">
                    <div className="app-item">
                      <div className="app-icon">📱</div>
                      <span>PhonePe</span>
                    </div>
                    <div className="app-item">
                      <div className="app-icon">💳</div>
                      <span>Paytm</span>
                    </div>
                    <div className="app-item">
                      <div className="app-icon">🏦</div>
                      <span>Google Pay</span>
                    </div>
                    <div className="app-item">
                      <div className="app-icon">💰</div>
                      <span>BHIM</span>
                    </div>
                    <div className="app-item">
                      <div className="app-icon">💵</div>
                      <span>Amazon Pay</span>
                    </div>
                    <div className="app-item">
                      <div className="app-icon">💸</div>
                      <span>Others</span>
                    </div>
                  </div>
                </div>

                <div className="info-box">
                  <div className="info-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </div>
                  <p>You will receive a payment request on your UPI app. Please approve it to complete the payment.</p>
                </div>

                <button
                  type="button"
                  className="submit-payment-btn"
                  onClick={handleSubmitPayment}
                  disabled={paymentDisabled}
                >
                  {submitButtonLabel()}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
